/**
 * What asset lists show of skin sets. A set's first skin is its default, and
 * lists (the Content Browser, the Mesh picker) show only it, noting how many
 * skins it has. The other skins are still in the catalog, and the Inspector's
 * Skin row is where they're chosen.
 *
 * This only decides listing; which model an entity shows is its Mesh field's.
 */
import type { SkinSet } from "./content";

export interface SkinListing {
  /** Whether a model appears in asset lists: every model but a set's non-default skins. */
  isListed(path: string): boolean;
  /** How many skins a set's default model stands for, or 0 for any other model. */
  skinsOf(path: string): number;
}

export function skinListing(sets: readonly SkinSet[] = []): SkinListing {
  const hidden = new Set(sets.flatMap((set) => set.skins.slice(1).map((skin) => skin.model)));
  const counts = new Map(
    sets.flatMap((set) => {
      const [first] = set.skins;
      return first ? [[first.model, set.skins.length] as const] : [];
    }),
  );
  return {
    isListed: (path) => !hidden.has(path),
    skinsOf: (path) => counts.get(path) ?? 0,
  };
}

/** " · 3 skins" for a set's default model, "" for anything else: a detail line's suffix. */
export function skinsDetail(listing: SkinListing, path: string): string {
  const count = listing.skinsOf(path);
  return count > 1 ? ` · ${count} skins` : "";
}
